import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  Drawer,
  Card,
  Editable,
  Spinner,
  Grid,
  NativeSelectRoot,
  NativeSelectField,
  Button,
  CollapsibleRoot,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@chakra-ui/react'
import {
  ChevronUp,
  ChevronDown,
  X,
  Trash2,
  Pencil,
  Check,
  Clock,
  CalendarClock,
  FileText,
  Mail as MailIcon,
  Home,
  ChevronRight,
} from 'lucide-react'
import { useOwner, useUpdateOwner, useDeleteOwner } from '../hooks/useOwners'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { ConfirmDeleteDialog } from '@/shared/components/ConfirmDeleteDialog'

interface OwnerDetailDrawerProps {
  ownerId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

// Composant interne pour les champs éditables
interface EditableFieldProps {
  label: string
  value: string | number | null | undefined
  fieldName: string
  placeholder?: string
  type?: 'text' | 'number'
  onUpdate: (fieldName: string, value: string | number) => Promise<void>
}

function EditableField({
  label,
  value,
  fieldName,
  placeholder = 'Non renseigné',
  type = 'text',
  onUpdate,
}: EditableFieldProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [currentValue, setCurrentValue] = useState(value?.toString() || '')

  useEffect(() => {
    setCurrentValue(value?.toString() || '')
  }, [value])

  const handleSubmit = async (details: { value: string }) => {
    const newValue = details.value
    if (newValue !== (value?.toString() || '')) {
      setIsLoading(true)
      try {
        const finalValue = type === 'number' && newValue ? Number(newValue) : newValue
        await onUpdate(fieldName, finalValue)
      } catch (error) {
        console.error('Error updating field:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <Editable.Root
        value={currentValue}
        onValueChange={(e) => setCurrentValue(e.value)}
        onValueCommit={handleSubmit}
        placeholder={placeholder}
        disabled={isLoading}
      >
        <Editable.Preview
          fontWeight="medium"
          color={currentValue ? 'inherit' : 'gray.400'}
          px={2}
          py={1}
          borderRadius="md"
          display="flex"
          alignItems="center"
          gap={2}
          _hover={{ bg: 'gray.50' }}
        />
        <Editable.Input px={2} py={1} type={type} />
        <Editable.Control display="flex" gap={1} alignItems="center">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <Editable.EditTrigger asChild>
                <IconButton variant="ghost" size="xs" aria-label="Edit">
                  <Pencil size={14} />
                </IconButton>
              </Editable.EditTrigger>
              <Editable.SubmitTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="green" aria-label="Submit">
                  <Check size={14} />
                </IconButton>
              </Editable.SubmitTrigger>
              <Editable.CancelTrigger asChild>
                <IconButton variant="ghost" size="xs" colorPalette="red" aria-label="Cancel">
                  <X size={14} />
                </IconButton>
              </Editable.CancelTrigger>
            </>
          )}
        </Editable.Control>
      </Editable.Root>
    </Box>
  )
}

// Composant pour le sélecteur de mode de contact
interface EditableSelectFieldProps {
  label: string
  value: string | null
  fieldName: string
  options: { value: string; label: string }[]
  onUpdate: (fieldName: string, value: string) => Promise<void>
}

function EditableSelectField({
  label,
  value,
  fieldName,
  options,
  onUpdate,
}: EditableSelectFieldProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    if (newValue !== (value || '')) {
      setIsLoading(true)
      try {
        await onUpdate(fieldName, newValue)
      } catch (error) {
        console.error('Error updating field:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <NativeSelectRoot>
        <NativeSelectField
          value={value || ''}
          onChange={handleChange}
          opacity={isLoading ? 0.6 : 1}
          pointerEvents={isLoading ? 'none' : 'auto'}
          fontWeight="medium"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelectField>
      </NativeSelectRoot>
      {isLoading && <Spinner size="sm" mt={1} />}
    </Box>
  )
}

export function OwnerDetailDrawer({
  ownerId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
}: OwnerDetailDrawerProps) {
  const { t } = useTranslation()
  const { data: owner, isLoading, error } = useOwner(ownerId)
  const updateOwner = useUpdateOwner()
  const deleteOwner = useDeleteOwner()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Separate active and former ownerships
  const activeOwnerships = owner?.ownerships?.filter((o) => !o.end_date) || []
  const formerOwnerships = owner?.ownerships?.filter((o) => o.end_date) || []

  // Options pour le mode de contact
  const contactOptions = [
    { value: 'EMAIL', label: 'Email' },
    { value: 'PAPER', label: 'Courrier papier' },
  ]

  const handleFieldUpdate = async (fieldName: string, value: string | number) => {
    if (!ownerId) return

    try {
      await updateOwner.mutateAsync({
        id: ownerId,
        updates: {
          [fieldName]: value || null,
        },
      })
    } catch (error) {
      console.error('Error updating owner:', error)
      alert(t('common.error'))
    }
  }

  const handleDelete = async () => {
    if (!ownerId) return

    try {
      await deleteOwner.mutateAsync(ownerId)
      setShowDeleteDialog(false)
      onClose()
    } catch (error) {
      console.error('Error deleting owner:', error)
      alert(t('common.error'))
    }
  }

  const handleInteractOutside = (e: any) => {
    const target = e.detail.originalEvent.target as HTMLElement

    // Chercher la ligne du tableau cliquée (uniquement les lignes de données, pas le header)
    const tableRow = target.closest('[data-owner-row]') as HTMLElement
    const ownerCard = target.closest('[data-owner-card]') as HTMLElement

    if (tableRow) {
      // Récupérer l'ID de l'owner depuis la ligne
      // La ligne a un onClick qui sera déclenché, donc on ne fait rien ici
      // Le handleOwnerClick parent sera appelé automatiquement
      return
    }

    if (ownerCard) {
      // Même chose pour les cards
      return
    }

    // Clic vraiment en dehors (ni tableau ni cards) → Fermer le drawer
    onClose()
  }

  const getContactBadgeColor = (contact: string | null) => {
    return contact === 'EMAIL' ? 'blue' : 'orange'
  }

  return (
    <>
      <Drawer.Root
        open={isOpen}
        placement="end"
        size="lg"
        modal={false}
        onInteractOutside={handleInteractOutside}
      >
        <Drawer.Positioner pointerEvents="none">
          <Drawer.Content maxW="800px" pointerEvents="auto">
            <Drawer.Header borderBottomWidth="1px" py={4}>
              <Box display="flex" alignItems="center" w="full" gap={3}>
                {/* Navigation arrows - Left */}
                <Box display="flex" gap={1} flexShrink={0}>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={onNavigatePrevious}
                    disabled={!hasPrevious}
                    aria-label={t('common.previous')}
                    title={t('common.previous')}
                  >
                    <ChevronUp size={18} />
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={onNavigateNext}
                    disabled={!hasNext}
                    aria-label={t('common.next')}
                    title={t('common.next')}
                  >
                    <ChevronDown size={18} />
                  </IconButton>
                </Box>

                {/* Title - Center */}
                <Box flex={1} minW={0}>
                  {owner && (
                    <>
                      <Heading
                        size="md"
                        lineHeight="1.3"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {owner.first_name} {owner.last_name}
                      </Heading>
                      <HStack gap={1.5} mt={1}>
                        <Badge
                          colorPalette={getContactBadgeColor(owner.preferred_contact)}
                          size="sm"
                        >
                          {owner.preferred_contact === 'EMAIL' ? 'EMAIL' : 'COURRIER'}
                        </Badge>
                      </HStack>
                    </>
                  )}
                  {!owner && <Heading size="md">{t('owners.details')}</Heading>}
                </Box>

                {/* Actions menu and close - Right */}
                <Box display="flex" gap={2} flexShrink={0}>
                  {owner && (
                    <>
                      {/* Mock buttons */}
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => alert('Génération de document (à venir)')}
                        aria-label="Générer document"
                        title="Générer document"
                      >
                        <FileText size={18} />
                      </IconButton>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => alert('Envoyer email (à venir)')}
                        aria-label="Envoyer email"
                        title="Envoyer email"
                      >
                        <MailIcon size={18} />
                      </IconButton>
                    </>
                  )}
                  <IconButton variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
                    <X size={18} />
                  </IconButton>
                </Box>
              </Box>
            </Drawer.Header>

            <Drawer.Body p={0}>
              {isLoading && (
                <Box p={6}>
                  <LoadingState message={t('common.loading')} />
                </Box>
              )}
              {error && (
                <Box p={6}>
                  <ErrorState message={t('common.error')} />
                </Box>
              )}
              {owner && (
                <>
                  {/* Timestamps */}
                  <HStack
                    gap={3}
                    px={6}
                    pt={4}
                    pb={3}
                    bg="gray.50"
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                    flexWrap="wrap"
                  >
                    <HStack gap={2} flex={1} minW="200px">
                      <Box color="blue.500">
                        <CalendarClock size={16} />
                      </Box>
                      <VStack align="flex-start" gap={0}>
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {t('common.created_on')}
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                          {new Date(owner.created_at ?? '').toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Text>
                      </VStack>
                    </HStack>

                    {owner.updated_at && owner.updated_at !== owner.created_at && (
                      <HStack gap={2} flex={1} minW="200px">
                        <Box color="green.500">
                          <Clock size={16} />
                        </Box>
                        <VStack align="flex-start" gap={0}>
                          <Text fontSize="xs" color="gray.600" fontWeight="medium">
                            {t('common.last_updated')}
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                            {new Date(owner.updated_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </Text>
                        </VStack>
                      </HStack>
                    )}
                  </HStack>

                  <Box px={6} py={6}>
                    <VStack align="stretch" gap={6}>
                      {/* Informations générales */}
                      <Card.Root variant="outline">
                        <Card.Header>
                          <Heading size="sm">{t('owners.sections.general_info')}</Heading>
                        </Card.Header>
                        <Card.Body>
                          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                            <EditableField
                              label={t('owners.fields.first_name')}
                              value={owner.first_name}
                              fieldName="first_name"
                              onUpdate={handleFieldUpdate}
                            />
                            <EditableField
                              label={t('owners.fields.last_name')}
                              value={owner.last_name}
                              fieldName="last_name"
                              onUpdate={handleFieldUpdate}
                            />
                            <EditableField
                              label={t('owners.fields.email')}
                              value={owner.email}
                              fieldName="email"
                              placeholder="Non renseigné"
                              onUpdate={handleFieldUpdate}
                            />
                            <EditableField
                              label={t('owners.fields.phone')}
                              value={owner.phone}
                              fieldName="phone"
                              placeholder="Non renseigné"
                              onUpdate={handleFieldUpdate}
                            />
                            <Box gridColumn={{ base: '1', md: '1 / -1' }}>
                              <EditableField
                                label={t('owners.fields.address')}
                                value={owner.address}
                                fieldName="address"
                                placeholder="Non renseignée"
                                onUpdate={handleFieldUpdate}
                              />
                            </Box>
                            <EditableSelectField
                              label={t('owners.fields.preferred_contact')}
                              value={owner.preferred_contact}
                              fieldName="preferred_contact"
                              options={contactOptions}
                              onUpdate={handleFieldUpdate}
                            />
                          </Grid>
                        </Card.Body>
                      </Card.Root>

                      {/* Lots possédés */}
                      <Card.Root variant="outline">
                        <Card.Header>
                          <HStack justify="space-between">
                            <Heading size="sm">{t('owners.sections.owned_units')}</Heading>
                            <Badge colorPalette="brand" size="sm">
                              {activeOwnerships.length} {activeOwnerships.length === 1 ? 'lot' : 'lots'}
                            </Badge>
                          </HStack>
                        </Card.Header>
                        <Card.Body>
                          {activeOwnerships.length > 0 ? (
                            <VStack align="stretch" gap={3}>
                              {activeOwnerships.map((ownership) => (
                                <HStack
                                  key={ownership.id}
                                  p={3}
                                  bg="gray.50"
                                  borderRadius="md"
                                  borderWidth="1px"
                                  borderColor="gray.200"
                                  justify="space-between"
                                >
                                  <HStack gap={2}>
                                    <Home size={16} color="var(--chakra-colors-brand-500)" />
                                    <VStack align="flex-start" gap={0}>
                                      <Text fontWeight="semibold" fontSize="sm">
                                        Lot {ownership.units?.unit_number}
                                      </Text>
                                      <Text fontSize="xs" color="gray.600">
                                        {ownership.units?.residences?.residence_key
                                          ? `${ownership.units.residences.residence_key} - ${ownership.units.residences.name}`
                                          : ownership.units?.residences?.name}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                  {!(ownership.share_numerator === 1 && ownership.share_denominator === 1) && (
                                    <Badge colorPalette="green" size="sm">
                                      Quote-part : {ownership.share_numerator}/{ownership.share_denominator}
                                    </Badge>
                                  )}
                                </HStack>
                              ))}
                            </VStack>
                          ) : (
                            <Text color="gray.500" fontSize="sm">
                              Aucun lot possédé
                            </Text>
                          )}
                        </Card.Body>
                      </Card.Root>

                      {/* Historique (collapsible) */}
                      {formerOwnerships.length > 0 && (
                        <CollapsibleRoot>
                          <Card.Root variant="outline">
                            <Card.Header>
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  width="full"
                                  justifyContent="space-between"
                                  p={0}
                                  _hover={{ bg: 'transparent' }}
                                >
                                  <HStack justify="space-between" width="full">
                                    <Heading size="sm">{t('owners.sections.history')}</Heading>
                                    <HStack gap={2}>
                                      <Badge colorPalette="gray" size="sm">
                                        {formerOwnerships.length}
                                      </Badge>
                                      <ChevronRight size={16} />
                                    </HStack>
                                  </HStack>
                                </Button>
                              </CollapsibleTrigger>
                            </Card.Header>
                            <CollapsibleContent>
                              <Card.Body>
                                <VStack align="stretch" gap={3}>
                                  {formerOwnerships.map((ownership) => (
                                    <HStack
                                      key={ownership.id}
                                      p={3}
                                      bg="gray.50"
                                      borderRadius="md"
                                      borderWidth="1px"
                                      borderColor="gray.200"
                                      justify="space-between"
                                    >
                                      <HStack gap={2}>
                                        <Home size={16} color="var(--chakra-colors-gray-400)" />
                                        <VStack align="flex-start" gap={0}>
                                          <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                                            Lot {ownership.units?.unit_number}
                                          </Text>
                                          <Text fontSize="xs" color="gray.500">
                                            {new Date(ownership.start_date).toLocaleDateString('fr-FR')} -{' '}
                                            {ownership.end_date
                                              ? new Date(ownership.end_date).toLocaleDateString('fr-FR')
                                              : 'Aujourd\'hui'}
                                          </Text>
                                        </VStack>
                                      </HStack>
                                      {!(ownership.share_numerator === 1 && ownership.share_denominator === 1) && (
                                        <Badge colorPalette="gray" size="sm">
                                          Quote-part : {ownership.share_numerator}/{ownership.share_denominator}
                                        </Badge>
                                      )}
                                    </HStack>
                                  ))}
                                </VStack>
                              </Card.Body>
                            </CollapsibleContent>
                          </Card.Root>
                        </CollapsibleRoot>
                      )}

                      {/* Actions */}
                      <Card.Root variant="outline" borderColor="red.200">
                        <Card.Header>
                          <Heading size="sm" color="red.600">
                            {t('owners.sections.actions')}
                          </Heading>
                        </Card.Header>
                        <Card.Body>
                          <Button
                            colorPalette="red"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDeleteDialog(true)}
                          >
                            <Trash2 size={16} />
                            {t('owners.delete')}
                          </Button>
                          <Text fontSize="xs" color="gray.600" mt={2}>
                            {t('owners.delete_owner_warning')}
                          </Text>
                        </Card.Body>
                      </Card.Root>
                    </VStack>
                  </Box>
                </>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {/* Delete confirmation dialog */}
      {owner && (
        <ConfirmDeleteDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          entityName={`${owner.first_name} ${owner.last_name}`}
          entityType="copropriétaire"
          warningMessage={t('owners.delete_confirm_message')}
        />
      )}
    </>
  )
}
